<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import ReportHeader from './components/ReportHeader.vue';
import Spinner from 'shared/components/Spinner.vue';

const { t } = useI18n();
const { accountId, accountScopedUrl } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

const REPORT_URL = import.meta.env.VITE_JIB_AI_REPORT_URL;
const REPORT_BEARER = import.meta.env.VITE_JIB_AI_REPORT_BEARER;
const MAX_COUNT = 50;

const hoursOptions = [24, 48, 72];
const selectedHours = ref(24);
const selectedInboxId = ref(null);

const loading = ref(false);
const error = ref(null);
const report = ref(null);

const isConfigured = computed(() => Boolean(REPORT_URL && REPORT_BEARER));

const fetchReport = async () => {
  if (!isConfigured.value) {
    error.value = t('DAILY_CHAT_SUMMARY.NOT_CONFIGURED');
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    const params = new URLSearchParams({
      account_id: accountId.value,
      hours: selectedHours.value,
      max_count: MAX_COUNT,
    });
    if (selectedInboxId.value) {
      params.set('inbox_id', selectedInboxId.value);
    }
    const res = await fetch(`${REPORT_URL}?${params}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${REPORT_BEARER}` },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
    report.value = await res.json();
  } catch (e) {
    error.value = e.message || t('DAILY_CHAT_SUMMARY.FETCH_FAILED');
    report.value = null;
  } finally {
    loading.value = false;
  }
};

onMounted(fetchReport);
watch([selectedHours, selectedInboxId], fetchReport);

const sentimentBuckets = computed(() => {
  const entries = report.value?.llm?.sentiment_per_conversation || [];
  return entries.reduce(
    (acc, { sentiment }) => {
      if (sentiment in acc) acc[sentiment] += 1;
      return acc;
    },
    { positive: 0, neutral: 0, negative: 0 }
  );
});

const conversationUrl = id => accountScopedUrl(`conversations/${id}`);
</script>

<template>
  <ReportHeader
    :header-title="t('DAILY_CHAT_SUMMARY.HEADER')"
    :header-description="t('DAILY_CHAT_SUMMARY.DESCRIPTION')"
  >
    <div class="flex items-center gap-2">
      <select
        v-model.number="selectedHours"
        class="px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-3 text-n-slate-12 text-body-small"
        :disabled="loading"
      >
        <option v-for="h in hoursOptions" :key="h" :value="h">
          {{ t('DAILY_CHAT_SUMMARY.LAST_N_HOURS', { n: h }) }}
        </option>
      </select>
      <select
        v-model="selectedInboxId"
        class="px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-3 text-n-slate-12 text-body-small"
        :disabled="loading"
      >
        <option :value="null">
          {{ t('DAILY_CHAT_SUMMARY.ALL_INBOXES') }}
        </option>
        <option v-for="ib in inboxes" :key="ib.id" :value="ib.id">
          {{ ib.name }}
        </option>
      </select>
      <button
        type="button"
        class="px-4 py-2 rounded-lg bg-n-brand text-white text-body-small disabled:opacity-50"
        :disabled="loading"
        @click="fetchReport"
      >
        {{ t('DAILY_CHAT_SUMMARY.REFRESH') }}
      </button>
    </div>
  </ReportHeader>

  <div v-if="loading" class="flex flex-col items-center gap-3 py-16">
    <Spinner size="medium" />
    <p class="text-n-slate-11 text-body-main">
      {{ t('DAILY_CHAT_SUMMARY.LOADING') }}
    </p>
  </div>

  <div
    v-else-if="error"
    class="rounded-lg border border-n-ruby-7 bg-n-ruby-3 px-4 py-3 text-n-ruby-11"
  >
    {{ t('DAILY_CHAT_SUMMARY.ERROR_PREFIX') }} {{ error }}
  </div>

  <div v-else-if="report" class="flex flex-col gap-4">
    <section class="rounded-xl bg-n-alpha-2 border border-n-weak p-5">
      <h2 class="text-heading-3 text-n-slate-12 mb-2">
        {{ t('DAILY_CHAT_SUMMARY.EXEC_SUMMARY') }}
      </h2>
      <p class="text-body-main text-n-slate-12">
        {{ report.llm.executive_summary }}
      </p>
    </section>

    <section class="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div class="rounded-xl bg-n-alpha-2 border border-n-weak p-4">
        <p class="text-body-small text-n-slate-11">
          {{ t('DAILY_CHAT_SUMMARY.STATS.IN_WINDOW') }}
        </p>
        <p class="text-heading-2 text-n-slate-12">
          {{ report.stats.total_in_window }}
        </p>
      </div>
      <div class="rounded-xl bg-n-alpha-2 border border-n-weak p-4">
        <p class="text-body-small text-n-slate-11">
          {{ t('DAILY_CHAT_SUMMARY.STATS.ANALYZED') }}
        </p>
        <p class="text-heading-2 text-n-slate-12">
          {{ report.stats.total_fetched }}
        </p>
      </div>
      <div class="rounded-xl bg-n-alpha-2 border border-n-weak p-4">
        <p class="text-body-small text-n-slate-11">
          {{ t('DAILY_CHAT_SUMMARY.STATS.OPEN') }}
        </p>
        <p class="text-heading-2 text-n-slate-12">
          {{ report.stats.by_status.open || 0 }}
        </p>
      </div>
      <div class="rounded-xl bg-n-alpha-2 border border-n-weak p-4">
        <p class="text-body-small text-n-slate-11">
          {{ t('DAILY_CHAT_SUMMARY.STATS.RESOLVED') }}
        </p>
        <p class="text-heading-2 text-n-slate-12">
          {{ report.stats.by_status.resolved || 0 }}
        </p>
      </div>
    </section>

    <section
      v-if="report.llm.attention_chats?.length"
      class="rounded-xl bg-n-alpha-2 border border-n-weak p-5"
    >
      <h2 class="text-heading-3 text-n-slate-12 mb-3">
        {{ t('DAILY_CHAT_SUMMARY.ATTENTION') }}
      </h2>
      <ul class="flex flex-col gap-2">
        <li
          v-for="chat in report.llm.attention_chats"
          :key="chat.conversation_id"
          class="flex justify-between gap-4 items-start"
        >
          <span class="text-body-main text-n-slate-12">{{ chat.reason }}</span>
          <a
            :href="conversationUrl(chat.conversation_id)"
            class="text-body-small text-n-brand whitespace-nowrap inline-flex items-center gap-1"
          >
            <span>{{ `#${chat.conversation_id}` }}</span>
            <span class="i-lucide-external-link size-3" />
          </a>
        </li>
      </ul>
    </section>

    <section
      v-if="report.llm.positive_highlights?.length"
      class="rounded-xl bg-n-alpha-2 border border-n-weak p-5"
    >
      <h2 class="text-heading-3 text-n-slate-12 mb-3">
        {{ t('DAILY_CHAT_SUMMARY.HIGHLIGHTS') }}
      </h2>
      <ul class="flex flex-col gap-2">
        <li
          v-for="chat in report.llm.positive_highlights"
          :key="chat.conversation_id"
          class="flex justify-between gap-4 items-start"
        >
          <span class="text-body-main text-n-slate-12">{{ chat.reason }}</span>
          <a
            :href="conversationUrl(chat.conversation_id)"
            class="text-body-small text-n-brand whitespace-nowrap inline-flex items-center gap-1"
          >
            <span>{{ `#${chat.conversation_id}` }}</span>
            <span class="i-lucide-external-link size-3" />
          </a>
        </li>
      </ul>
    </section>

    <section
      v-if="report.llm.themes?.length"
      class="rounded-xl bg-n-alpha-2 border border-n-weak p-5"
    >
      <h2 class="text-heading-3 text-n-slate-12 mb-3">
        {{ t('DAILY_CHAT_SUMMARY.THEMES') }}
      </h2>
      <ul class="flex flex-col gap-2">
        <li
          v-for="theme in report.llm.themes"
          :key="theme.theme"
          class="flex justify-between gap-4 items-center"
        >
          <span class="text-body-main text-n-slate-12">{{ theme.theme }}</span>
          <span class="text-body-small text-n-slate-11">
            {{ theme.count }} ·
            <a
              v-for="id in theme.conversation_ids"
              :key="id"
              :href="conversationUrl(id)"
              class="text-n-brand ml-1"
            >
              #{{ id }}
            </a>
          </span>
        </li>
      </ul>
    </section>

    <section class="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div class="rounded-xl bg-n-alpha-2 border border-n-weak p-5">
        <h2 class="text-heading-3 text-n-slate-12 mb-3">
          {{ t('DAILY_CHAT_SUMMARY.SENTIMENT.TITLE') }}
        </h2>
        <div class="grid grid-cols-3 gap-2 text-center">
          <div>
            <p class="text-heading-2 text-n-teal-11">
              {{ sentimentBuckets.positive }}
            </p>
            <p class="text-body-small text-n-slate-11">
              {{ t('DAILY_CHAT_SUMMARY.SENTIMENT.POSITIVE') }}
            </p>
          </div>
          <div>
            <p class="text-heading-2 text-n-slate-11">
              {{ sentimentBuckets.neutral }}
            </p>
            <p class="text-body-small text-n-slate-11">
              {{ t('DAILY_CHAT_SUMMARY.SENTIMENT.NEUTRAL') }}
            </p>
          </div>
          <div>
            <p class="text-heading-2 text-n-ruby-11">
              {{ sentimentBuckets.negative }}
            </p>
            <p class="text-body-small text-n-slate-11">
              {{ t('DAILY_CHAT_SUMMARY.SENTIMENT.NEGATIVE') }}
            </p>
          </div>
        </div>
      </div>

      <div class="rounded-xl bg-n-alpha-2 border border-n-weak p-5">
        <h2 class="text-heading-3 text-n-slate-12 mb-3">
          {{ t('DAILY_CHAT_SUMMARY.AI.TITLE') }}
        </h2>
        <dl class="grid grid-cols-3 gap-2 text-center">
          <div>
            <dd class="text-heading-2 text-n-slate-12">
              {{ report.llm.ai_engagement.ai_engaged_conversation_ids.length }}
            </dd>
            <dt class="text-body-small text-n-slate-11">
              {{ t('DAILY_CHAT_SUMMARY.AI.ENGAGED') }}
            </dt>
          </div>
          <div>
            <dd class="text-heading-2 text-n-slate-12">
              {{
                report.llm.ai_engagement.ai_e2e_resolved_conversation_ids.length
              }}
            </dd>
            <dt class="text-body-small text-n-slate-11">
              {{ t('DAILY_CHAT_SUMMARY.AI.RESOLVED') }}
            </dt>
          </div>
          <div>
            <dd class="text-heading-2 text-n-slate-12">
              {{ report.llm.ai_engagement.ai_handover_conversation_ids.length }}
            </dd>
            <dt class="text-body-small text-n-slate-11">
              {{ t('DAILY_CHAT_SUMMARY.AI.HANDOVER') }}
            </dt>
          </div>
        </dl>
      </div>
    </section>
  </div>
</template>
